import {focusWithFallbackTabIndex} from '@aboutyou/a11y';
import {styled} from '@linaria/react';
import React, {useEffect, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {mediaDesktopAndUp} from '../../../constants/customMedia';
import {
  DESKTOP_STICKY_HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
} from '../../../constants/heights';
import {Z_INDEXES} from '../../../constants/layers';

const SKIP_LINK_ATTRIBUTE = 'data-skip-link';

interface SkipLinkEntry {
  id: string;
  label: string;
}

// Tries each id in order and focuses the first one that exists, so a link
// can fall back to a secondary target when its primary one isn't rendered.
// Most targets (main, footer, a product tile, ...) aren't natively
// focusable, hence focusWithFallbackTabIndex rather than a plain focus().
const focusTarget = (targetId: string) => {
  const target = document.getElementById(targetId);
  if (!target) {
    return;
  }

  // Scrolling is handled separately, deferred a frame: some targets (e.g.
  // the footer) use `content-visibility: auto` and are laid out using a
  // placeholder size while off-screen, so scrolling immediately can
  // overshoot based on that stale size. By the next frame the browser has
  // rendered the now-focused content and its real size is known.
  focusWithFallbackTabIndex(target, {preventScroll: true});
  requestAnimationFrame(() => {
    target.scrollIntoView({block: 'start'});
  });
};

// Any element can opt into the skip-link list by rendering
// `data-skip-link="<label>"` on itself, instead of a page having to keep a
// separate, manually maintained list in sync with what it actually renders.
// querySelectorAll returns matches in document order, which also happens to
// be the tab order we want (e.g. main content before footer).
const readSkipLinks = (): SkipLinkEntry[] => {
  const elements = document.querySelectorAll<HTMLElement>(
    `[${SKIP_LINK_ATTRIBUTE}]`,
  );

  // Keyed by id so duplicates collapse to their first occurrence (Map
  // preserves insertion order, so this stays in document order). Duplicate
  // ids can happen (e.g. multiple product streams on one page each
  // rendering their own "first-product-tile"), and document.getElementById
  // always resolves to the first match regardless, so a second entry would
  // just be a redundant link to the same target.
  const byId = Array.from(elements).reduce((entries, element) => {
    if (element.id && !entries.has(element.id)) {
      entries.set(element.id, {
        id: element.id,
        label: element.getAttribute(SKIP_LINK_ATTRIBUTE)!,
      });
    }
    return entries;
  }, new Map<string, SkipLinkEntry>());

  return Array.from(byId.values());
};

// attributeFilter (below) only scopes attribute mutations, not childList/
// subtree ones, so a childList/subtree mutation is only relevant here if it
// actually added or removed a data-skip-link element — otherwise every DOM
// change anywhere on the page (e.g. infinite scroll) would trigger a rescan.
const mutationTouchesSkipLink = (mutations: MutationRecord[]): boolean =>
  mutations.some(mutation => {
    if (mutation.type === 'attributes') {
      return true;
    }
    return [...mutation.addedNodes, ...mutation.removedNodes].some(
      node =>
        node instanceof HTMLElement &&
        (node.hasAttribute(SKIP_LINK_ATTRIBUTE) ||
          node.querySelector(`[${SKIP_LINK_ATTRIBUTE}]`)),
    );
  });

export const SkipLinks: React.FC = () => {
  const [links, setLinks] = useState<SkipLinkEntry[]>([]);

  useEffect(() => {
    // The list can only be read from the live DOM (document.querySelectorAll),
    // which doesn't exist until after mount, so it can't be computed during
    // render like normal derived state.
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setLinks(readSkipLinks());

    // Elements tagged with data-skip-link can mount/unmount after this
    // component does (route changes, "load more", async sections), so the
    // list is kept in sync with the live DOM rather than scanned once.
    const observer = new MutationObserver(mutations => {
      if (mutationTouchesSkipLink(mutations)) {
        setLinks(readSkipLinks());
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [SKIP_LINK_ATTRIBUTE],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <SkipLinksWrapper>
      {links.map(({id, label}) => (
        <li key={id}>
          <StyledLink
            href={`#${id}`}
            tabIndex={0}
            onClick={event => {
              // Prevented so we can move focus ourselves instead of the browser's
              // native hash-jump, which only scrolls and doesn't reliably move
              // keyboard focus (and would also add a `#...` entry to the URL).
              event.preventDefault();
              focusTarget(id);
            }}
          >
            <span>{label}</span>
          </StyledLink>
        </li>
      ))}
    </SkipLinksWrapper>
  );
};

const SkipLinksWrapper = styled.ul`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: ${Z_INDEXES.MODAL_DIALOG_WRAPPER};
  transition: transform 0.3s;
  transform: translateY(-100%);
  // Off-screen positioning alone is visible in the overscroll area above the
  // page on mobile, so it is also transparent until focused. Not
  // visibility/display: that would remove the links from the tab order.
  opacity: 0;
  pointer-events: none;

  &:focus-within {
    transform: translateY(0%);
    opacity: 1;
    pointer-events: auto;
  }

  // Every skip-link target opts in solely by rendering data-skip-link (see
  // readSkipLinks above) — this rule keys off that same attribute so any
  // future target gets the correct landing offset automatically, without its
  // own component needing to know about the sticky header at all.
  // scrollIntoView (used in focusTarget above) respects scroll-margin-top
  // natively, so no scroll-math is needed on the JS side either. Declared
  // here (rather than a separate styled-components createGlobalStyle) so
  // Linaria extracts it to a static CSS file at build time instead of
  // shipping it as runtime JS in the eagerly-loaded bundle.
  :global([${SKIP_LINK_ATTRIBUTE}]) {
    scroll-margin-top: ${MOBILE_HEADER_HEIGHT}px;

    @media ${mediaDesktopAndUp} {
      scroll-margin-top: ${DESKTOP_STICKY_HEADER_HEIGHT}px;
    }
  }
`;

const StyledLink = styled.a`
  display: flex;
  justify-content: center;
  background: ${COLORS.Grey5};
  height: 30px;
  padding: 8px;

  &,
  &:hover,
  &:visited {
    text-decoration: none;
    color: inherit;
  }
`;
